import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
  Avatar,
  Combobox,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  OptionGroup,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PeopleTeam20Regular } from '@fluentui/react-icons';
import { useAadGroups, useProfiles } from '@/hooks/useVisibility';
import type { AadGroupSummary, ProfileSummary } from '@/services/api/endpoints';
import { groupLabel, userKey } from './useVisibilityForm';

// Panneau étroit : on borne le nombre d'options rendues plutôt que de dérouler
// tout l'annuaire ou tous les groupes du tenant, et on invite à affiner.
const MAX_ROWS = 50;

/** Valeur d'option d'un groupe. Préfixe distinct de `userKey` (`u:` / `o:`). */
function groupOptionValue(groupId: string): string {
  return `g:${groupId}`;
}

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0 },
  // Le combobox occupe toute la largeur du panneau, comme les champs voisins.
  combobox: { width: '100%' },
  listbox: {
    // Sans plafond, une recherche large déroule une liste plus haute que le
    // panneau et la popup devient inutilisable.
    maxHeight: '260px',
  },
  option: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, minWidth: 0 },
  optionName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  groupIcon: { display: 'flex', color: tokens.colorNeutralForeground3, flexShrink: 0 },
});

interface Props {
  /** Ne charge rien tant que la restriction n'est pas choisie. */
  enabled: boolean;
  selectedUserKeys: ReadonlySet<string>;
  selectedGroupIds: ReadonlySet<string>;
  onToggleUser: (profile: ProfileSummary) => void;
  onToggleGroup: (group: AadGroupSummary) => void;
}

/**
 * Sélecteur des ayants droit : une seule liste déroulante à choix multiples, où
 * personnes et groupes cohabitent dans deux sections distinctes (les choix
 * s'additionnent).
 *
 * RIEN N'EST AFFICHÉ NI CHARGÉ AVANT QU'ON SAISISSE QUELQUE CHOSE. Les deux
 * listes étaient auparavant dépliées en permanence dans la popup : celle-ci
 * dépassait la hauteur du panneau, et son ouverture rapatriait l'annuaire entier
 * plus les 381 groupes du tenant pour une sélection qui en retient deux ou trois.
 *
 * La composition d'un groupe ne se déplie plus ICI : une liste déroulante ne peut
 * pas contenir de sous-liste dépliable sans casser la navigation clavier de la
 * liste elle-même. Elle reste consultable dans le récapitulatif, juste en
 * dessous, une fois le groupe retenu — c'est-à-dire au moment où savoir « qui
 * cela représente » compte vraiment.
 */
export function PrincipalPicker({
  enabled,
  selectedUserKeys,
  selectedGroupIds,
  onToggleUser,
  onToggleGroup,
}: Props) {
  const styles = useStyles();
  const [q, setQ] = useState('');
  // On ne bombarde ni l'annuaire ni Graph à chaque frappe.
  const [debouncedQ] = useDebounce(q.trim(), 300);
  const hasQuery = debouncedQ.length > 0;

  const profiles = useProfiles(debouncedQ, enabled && hasQuery);
  const groups = useAadGroups(debouncedQ, enabled && hasQuery);

  const allProfiles = hasQuery ? (profiles.data ?? []) : [];
  const allGroups = hasQuery ? (groups.data ?? []) : [];
  const shownProfiles = allProfiles.slice(0, MAX_ROWS);
  const shownGroups = allGroups.slice(0, MAX_ROWS);

  // Une option ne transporte qu'une chaîne : on retrouve l'objet complet par sa
  // valeur au moment du clic (le formulaire a besoin du libellé, de la photo et
  // de la fonction, pas seulement de la clé).
  const byValue = useMemo(() => {
    const map = new Map<
      string,
      { kind: 'user'; profile: ProfileSummary } | { kind: 'group'; group: AadGroupSummary }
    >();
    for (const p of shownProfiles) {
      map.set(userKey({ email: p.email, oid: null }), { kind: 'user', profile: p });
    }
    for (const g of shownGroups) {
      map.set(groupOptionValue(g.groupId), { kind: 'group', group: g });
    }
    return map;
  }, [shownProfiles, shownGroups]);

  const selectedOptions = useMemo(
    () => [...selectedUserKeys, ...[...selectedGroupIds].map(groupOptionValue)],
    [selectedUserKeys, selectedGroupIds],
  );

  const loading = profiles.isLoading || groups.isLoading;
  const nothingFound = hasQuery && !loading && shownProfiles.length === 0 && shownGroups.length === 0;

  return (
    <div className={styles.wrap}>
      <Field label="Rechercher une personne ou un groupe">
        <Combobox
          className={styles.combobox}
          listbox={{ className: styles.listbox }}
          // `freeform` : le champ est une zone de recherche, pas un sélecteur de
          // valeur unique. Combiné à `value` piloté, il empêche Fluent de
          // remplacer la saisie par la liste des sélections — qui deviendrait
          // illisible dès trois personnes retenues, alors que le récapitulatif
          // juste en dessous fait déjà ce travail.
          freeform
          multiselect
          value={q}
          selectedOptions={selectedOptions}
          placeholder="Nom, e-mail, début de nom de groupe…"
          onChange={(e) => setQ(e.target.value)}
          onOptionSelect={(_e, data) => {
            const hit = data.optionValue ? byValue.get(data.optionValue) : undefined;
            if (!hit) return;
            if (hit.kind === 'user') onToggleUser(hit.profile);
            else onToggleGroup(hit.group);
          }}
        >
          {!hasQuery && (
            <Option value="__empty" disabled text="Saisissez un nom pour rechercher">
              Saisissez un nom pour rechercher une personne ou un groupe.
            </Option>
          )}

          {loading && (
            <Option value="__loading" disabled text="Recherche en cours">
              Recherche en cours…
            </Option>
          )}

          {nothingFound && (
            <Option value="__none" disabled text="Aucun résultat">
              Aucune personne ni groupe ne correspond à « {debouncedQ} ». Les groupes se recherchent
              par le début de leur nom.
            </Option>
          )}

          {shownProfiles.length > 0 && (
            <OptionGroup label={`Personnes (${allProfiles.length})`}>
              {shownProfiles.map((p) => (
                <Option
                  key={p.email}
                  value={userKey({ email: p.email, oid: null })}
                  // `text` sert au clavier et aux lecteurs d'écran : le nom, pas
                  // l'e-mail, qui n'est plus affiché.
                  text={p.displayName}
                >
                  <span className={styles.option} title={p.fonction ?? p.displayName}>
                    <Avatar
                      name={p.displayName}
                      image={p.photoUrl ? { src: p.photoUrl } : undefined}
                      color="colorful"
                      size={24}
                    />
                    <span className={styles.optionName}>{p.displayName}</span>
                  </span>
                </Option>
              ))}
              {allProfiles.length > shownProfiles.length && (
                <Option value="__more_users" disabled text="Trop de résultats">
                  {allProfiles.length} personnes trouvées, {shownProfiles.length} affichées. Précisez
                  la recherche.
                </Option>
              )}
            </OptionGroup>
          )}

          {shownGroups.length > 0 && (
            <OptionGroup label={`Groupes (${allGroups.length})`}>
              {shownGroups.map((g) => {
                const label = groupLabel(g);
                return (
                  <Option key={g.groupId} value={groupOptionValue(g.groupId)} text={label}>
                    <span className={styles.option} title={label}>
                      <span className={styles.groupIcon} aria-hidden>
                        <PeopleTeam20Regular />
                      </span>
                      <span className={styles.optionName}>{label}</span>
                    </span>
                  </Option>
                );
              })}
              {allGroups.length > shownGroups.length && (
                <Option value="__more_groups" disabled text="Trop de résultats">
                  {allGroups.length} groupes trouvés, {shownGroups.length} affichés. Précisez la
                  recherche.
                </Option>
              )}
            </OptionGroup>
          )}
        </Combobox>
      </Field>

      {/* Une panne d'annuaire ou de Graph se dit, mais sous le champ : dans la
          liste déroulante, le message disparaîtrait à la première frappe. */}
      {profiles.error && (
        <MessageBar intent="error">
          <MessageBarBody>Annuaire indisponible : {profiles.error.message}</MessageBarBody>
        </MessageBar>
      )}
      {groups.error && (
        <MessageBar intent="error">
          <MessageBarBody>Groupes indisponibles : {groups.error.message}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
